import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { IconLeft, IconRight, IconSearch } from "../../../../assets/icons";
import {
  Badge,
  Button,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Table,
} from "antd";
import api from "../../../../api/api";
import { getFormattedNumber } from "@utils";
import moment from "moment";
import { ORDER_STATUS } from "../../../../constants";
import _ from "lodash";

const RootContainer = styled.div`
  .ant-picker {
    height: 48px;
  }
`;

const Topbar = styled.div`
  padding-top: 8px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const Card = styled.div`
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  background: ${(props) => props.theme.backgroundColor};
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #1d1014;
`;

const BoldText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d1014;
`;

const ReportDetails = () => {
  const navigate = useNavigate();
  const { admin_id } = useParams();
  const url_params = new URLSearchParams(window.location.search);
  const name = url_params.get("name");
  const username = url_params.get("username");
  const [countLoading, setCountLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [data, setData] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [dates, setDates] = useState([]);

  useEffect(() => {
    getAdminOrderCounts();

    return () => {
      debouncedResults.cancel();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getAdminOrders(keyword, dates);

    // eslint-disable-next-line
  }, [keyword, dates]);

  const getAdminOrderCounts = () => {
    api
      .getAdminOrderCounts(admin_id)
      .then(({ data }) => {
        setCountLoading(false);
        setCounts(data);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const getAdminOrders = (keyword, dates) => {
    setLoading(true);
    setData([]);
    api
      .getAdminOrders({
        id: admin_id,
        keyword,
        date_from: dates[0]?.toISOString(),
        date_to: dates[1]?.toISOString(),
      })
      .then(({ data: { data } }) => {
        setLoading(false);
        setData(data);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error);
      });
  };

  const handleDateChange = (dates) => {
    setDates(dates ? dates : []);
  };

  const goBack = () => {
    navigate(-1);
  };

  const goToOrderDetails = (item) => {
    navigate(item.id);
  };

  const getOrderStatus = (status) => {
    switch (status) {
      case ORDER_STATUS.PENDING:
        return <Badge color={"orange"} text="Pending" />;
      case ORDER_STATUS.UNPAID:
        return <Badge color={"purple"} text="Unpaid" />;
      case ORDER_STATUS.CANCELLED:
        return <Badge color={"red"} text="Cancelled" />;
      case ORDER_STATUS.CONFIRMED:
        return <Badge color={"green"} text="Confirmed" />;
      case ORDER_STATUS.COMPLETED:
        return <Badge color={"blue"} text="Pickup" />;
      default:
        return "";
    }
  };

  const debouncedResults = useMemo(() => {
    return _.debounce((e) => {
      setKeyword(e.target.value.trim());
    }, 1000);
  }, []);

  const columns = [
    {
      key: "created_at",
      dataIndex: "created_at",
      title: "Date Time",
      // sorter: (a, b) =>
      //   moment(a.created_at)
      //     .format("DD MMM YYYY, HH:mm")
      //     .localeCompare(moment(b.created_at).format("DD MMM YYYY, HH:mm")),
      render: (_, record) =>
        moment(record.created_at).format("DD MMM YYYY, HH:mm"),
    },
    {
      key: "order_id",
      dataIndex: "order_id",
      title: "Order ID",
    },
    {
      key: "user",
      dataIndex: "user",
      title: "Customer ID",
      render: (_, record) => record.user?.username,
    },
    {
      key: "total_quantity",
      dataIndex: "total_quantity",
      title: "Qty / Amount",
      render: (_, record) =>
        `${getFormattedNumber(record.total_quantity)} / ${getFormattedNumber(
          record.final_amount
        )}`,
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      render: (_, record) => getOrderStatus(record.status),
    },
    {
      key: "action",
      render: (_, record) => (
        <IconRight onClick={() => goToOrderDetails(record)} />
      ),
    },
  ];

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          Back
        </Button>
        <TitleWrapper>
          <GrayTitle>Admin Report</GrayTitle>
          <IconRight />
          <Title>{`${name} (${username})`}</Title>
        </TitleWrapper>
      </Topbar>

      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Total Orders</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.totalCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Pending Orders</Text>
            <BoldText>
              {countLoading
                ? "..."
                : getFormattedNumber(counts.pendingCount || 0)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Unpaid Orders</Text>
            <BoldText>
              {countLoading
                ? "..."
                : getFormattedNumber(counts.unpaidCount || 0)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Confirmed Orders</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.confirmedCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Pickup Orders</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.completedCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>Cancelled Orders</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.cancelledCount)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Input
            placeholder="Search by order id"
            prefix={<IconSearch />}
            onChange={debouncedResults}
            allowClear
          />
        </Col>
        <Col xs={0} md={8} />
        <Col xs={24} md={8}>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            placeholder={["Start Date", "End Date"]}
            format={"DD MMM YYYY"}
            onChange={handleDateChange}
          />
        </Col>

        <Col xs={24}>
          <Table
            dataSource={data
              ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map((item) => item)}
            columns={columns}
            loading={loading}
            pagination={false}
          />
        </Col>
      </Row>
    </RootContainer>
  );
};

export default ReportDetails;
